/**
 * Local player progress (Phase 5): best time, its splits, completions,
 * attempts and recent valid times per record key. A record key is the track's
 * gameplay fingerprint plus the movement configuration and enabled rules
 * (Simulation.recordKey), so a time never moves to different geometry or
 * different physics.
 *
 * One JSON record in localStorage. Stored data is untrusted: every entry is
 * validated on load, the number of entries is capped (least recently played
 * are dropped), and when storage is unavailable progress lives in memory for
 * the session. Best times written by the Phase 1–4 LocalRecordStore are still
 * read, so earlier records are not lost.
 */
import type { RecordStore } from './RecordStore';
import { mergeBestSectors, sectors, type Split } from '../replay/RunTiming';

export interface TrackProgress {
  /** Best valid full-run time, seconds. */
  best: number | null;
  /** Checkpoint splits of the best run (checkpoint index and run time). */
  bestSplits: Split[];
  /** Best time per sector over all valid full runs, keyed "start>0", "0>1", "1>finish" (RunTiming.sectorKey). */
  bestSectors: Record<string, number>;
  /** Valid full-run finishes. */
  completions: number;
  /** Full-run attempts started (leaving the start zone). */
  attempts: number;
  /** Most recent valid times, newest last. */
  recent: number[];
  firstCompletedAt: number | null;
  lastPlayedAt: number;
}

const KEY = 'xernous.progress.v1';
const LEGACY_PREFIX = 'xernous.best.';
export const MAX_ENTRIES = 400;
export const MAX_RECENT = 10;
const MAX_SPLITS = 64;
const MAX_SECTORS = 128;
const SECTOR_KEY = /^(start|\d{1,3})>(finish|\d{1,3})$/;
/** A day: anything longer in a stored record is corrupt. */
const MAX_SECONDS = 86_400;
const MAX_KEY_LENGTH = 200;

const validTime = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0 && v < MAX_SECONDS;
const count = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 1e9 ? v : 0);
const stamp = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0);

export function emptyProgress(): TrackProgress {
  return { best: null, bestSplits: [], bestSectors: {}, completions: 0, attempts: 0, recent: [], firstCompletedAt: null, lastPlayedAt: 0 };
}

function sanitizeEntry(raw: unknown): TrackProgress | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const list = (v: unknown, cap: number): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0 && x < MAX_SECONDS).slice(-cap) : []);
  const best = validTime(r['best']) ? r['best'] : null;
  return {
    best,
    bestSplits: best === null ? [] : splitList(r['bestSplits']),
    bestSectors: sectorTable(r['bestSectors']),
    completions: count(r['completions']),
    attempts: count(r['attempts']),
    recent: list(r['recent'], MAX_RECENT).filter((x) => x > 0),
    firstCompletedAt: typeof r['firstCompletedAt'] === 'number' && Number.isFinite(r['firstCompletedAt']) ? r['firstCompletedAt'] : null,
    lastPlayedAt: stamp(r['lastPlayedAt']),
  };
}

/** Splits as {index, time}; Phase 5 stored bare times in checkpoint order, read as indices 0, 1, 2… */
function splitList(v: unknown): Split[] {
  if (!Array.isArray(v)) return [];
  const out: Split[] = [];
  for (const [i, x] of v.slice(0, MAX_SPLITS).entries()) {
    const s = typeof x === 'number' ? { index: i, time: x } : (x as Partial<Split> | null);
    if (s && typeof s === 'object' && Number.isInteger(s.index) && s.index! >= 0 && s.index! < 1000 && typeof s.time === 'number' && Number.isFinite(s.time) && s.time >= 0 && s.time < MAX_SECONDS) out.push({ index: s.index!, time: s.time });
  }
  return out;
}

function sectorTable(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return out;
  let n = 0;
  for (const [k, t] of Object.entries(v as Record<string, unknown>)) {
    if (n >= MAX_SECTORS) break;
    if (SECTOR_KEY.test(k) && validTime(t)) {
      out[k] = t;
      n++;
    }
  }
  return out;
}

/** Parses a stored progress record; anything malformed is dropped entry by entry. */
export function parseProgress(text: string | null): Map<string, TrackProgress> {
  const out = new Map<string, TrackProgress>();
  if (!text) return out;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return out;
  }
  const entries = (raw as { entries?: unknown } | null)?.entries;
  if (typeof entries !== 'object' || entries === null || Array.isArray(entries)) return out;
  for (const [key, value] of Object.entries(entries as Record<string, unknown>)) {
    if (key.length > MAX_KEY_LENGTH || key === '__proto__') continue;
    const e = sanitizeEntry(value);
    if (e) out.set(key, e);
  }
  return out;
}

export class ProgressStore implements RecordStore {
  private entries: Map<string, TrackProgress>;
  /** The stored text `entries` was last read from or written as (another tab may have changed it since). */
  private storedText: string | null = null;
  /** False once a write failed: progress is then kept for this session only. */
  persistent = true;

  constructor(
    private readonly storage: Storage | null = safeLocalStorage(),
    private readonly now: () => number = Date.now,
  ) {
    this.entries = new Map();
    this.sync();
    if (!this.storage) this.persistent = false;
  }

  /**
   * Picks up progress another tab stored since this one last read or wrote it, so a stale tab never
   * writes over a better time. Skipped while writes are failing: the in-memory copy is then the only one.
   */
  private sync(): void {
    if (!this.persistent) return;
    const text = this.read(KEY);
    if (text === this.storedText) return;
    this.storedText = text;
    const now = this.now();
    // A stamp from the future would make a fresh entry look oldest and get it evicted first.
    this.entries = new Map([...parseProgress(text)].map(([k, e]) => [k, { ...e, lastPlayedAt: Math.min(e.lastPlayedAt, now) }]));
  }

  /** Progress for a record key (a fresh empty record when none is stored). */
  get(key: string): TrackProgress {
    this.sync();
    const e = this.entries.get(key);
    if (e) return { ...e, bestSplits: e.bestSplits.map((s) => ({ ...s })), bestSectors: { ...e.bestSectors }, recent: [...e.recent] };
    const legacy = Number(this.read(LEGACY_PREFIX + key));
    return validTime(legacy) ? { ...emptyProgress(), best: legacy } : emptyProgress();
  }

  best(key: string): number | null {
    return this.get(key).best;
  }

  /** RecordStore: stores `seconds` when it beats the best. Splits arrive with completed(). */
  submit(key: string, seconds: number): boolean {
    if (!validTime(seconds)) return false;
    const e = this.get(key);
    if (e.best !== null && e.best <= seconds) return false;
    this.put(key, { ...e, best: seconds, bestSplits: [], lastPlayedAt: this.now() });
    return true;
  }

  /** A valid full-run finish: counts it, keeps it in the recent list, merges its sectors into the bests and, for a new best, keeps its splits. */
  completed(key: string, seconds: number, splits: readonly Split[]): void {
    if (!validTime(seconds)) return;
    const e = this.get(key);
    const isBest = e.best === seconds;
    const clean = splitList(splits);
    const merged = mergeBestSectors(new Map(Object.entries(e.bestSectors)), sectors(clean, seconds));
    this.put(key, {
      ...e,
      completions: e.completions + 1,
      recent: [...e.recent, seconds].slice(-MAX_RECENT),
      bestSplits: isBest ? clean : e.bestSplits,
      bestSectors: sectorTable(Object.fromEntries(merged)),
      firstCompletedAt: e.firstCompletedAt ?? this.now(),
      lastPlayedAt: this.now(),
    });
  }

  attemptStarted(key: string): void {
    const e = this.get(key);
    this.put(key, { ...e, attempts: e.attempts + 1, lastPlayedAt: this.now() });
  }

  /** Removes every stored record (the settings screen's reset). */
  clear(): void {
    this.entries.clear();
    this.storedText = null;
    try {
      this.storage?.removeItem(KEY);
      const s = this.storage;
      if (s) {
        const legacy: string[] = [];
        for (let i = 0; i < s.length; i++) {
          const k = s.key(i);
          if (k?.startsWith(LEGACY_PREFIX)) legacy.push(k);
        }
        for (const k of legacy) s.removeItem(k);
      }
    } catch (err: unknown) {
      console.warn('ProgressStore: could not clear stored progress', err);
    }
  }

  get size(): number {
    this.sync();
    return this.entries.size;
  }

  private put(key: string, entry: TrackProgress): void {
    if (key.length > MAX_KEY_LENGTH) return;
    this.entries.set(key, entry);
    if (this.entries.size > MAX_ENTRIES) {
      const oldest = [...this.entries].sort((a, b) => a[1].lastPlayedAt - b[1].lastPlayedAt).slice(0, this.entries.size - MAX_ENTRIES);
      for (const [k] of oldest) this.entries.delete(k);
    }
    this.write();
  }

  private read(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private write(): void {
    if (!this.storage) return;
    try {
      const text = JSON.stringify({ version: 1, entries: Object.fromEntries(this.entries) });
      this.storage.setItem(KEY, text);
      this.storedText = text;
      this.persistent = true;
    } catch (err: unknown) {
      this.persistent = false;
      console.warn('ProgressStore: progress could not be saved; it is kept for this session only', err);
    }
  }
}

/**
 * The game's record store for one track: only the track's baseline record key
 * (the shipped movement rules) can set a record. A run under tuned or
 * experimental movement has a different key and is never stored.
 */
export class CompetitiveRecords implements RecordStore {
  constructor(
    private readonly store: ProgressStore,
    readonly allowedKey: string,
  ) {}

  best(key: string): number | null {
    return key === this.allowedKey ? this.store.best(key) : null;
  }

  submit(key: string, seconds: number): boolean {
    return key === this.allowedKey && this.store.submit(key, seconds);
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
